import { type ChangeEventHandler, type ReactNode, forwardRef } from 'react'
import { cx } from 'utils/cx'

import fukkuStyles from 'fukku/styles/classes.module.scss'
import text from 'fukku/styles/texts.module.scss'
import styles from './ListExpandible.module.scss'

export type ExpandibleElement = {
	title: ReactNode | string
	subtitle?: ReactNode | string
	id: string
	value: string
	expanded: boolean
	description?: ReactNode | string
	icon?: ReactNode
	'data-testid'?: string
}

export type ListExpandibleProps = {
	readonly options: ExpandibleElement[]
	readonly onExpand: (value: string) => void
	readonly srText: string
	readonly isFreeContent?: boolean
	readonly 'data-testid'?: string
	readonly name: string
}

export const ListExpandible = forwardRef<HTMLInputElement, ListExpandibleProps>(
	function ListExpandible(
		{
			options,
			onExpand,
			srText,
			isFreeContent,
			'data-testid': dataTestId,
			name,
		},
		ref
	) {
		const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
			const { value } = event.target as HTMLInputElement
			onExpand(value)
		}

		return (
			<fieldset data-testid={dataTestId} className={styles.fieldset}>
				<legend className={fukkuStyles.srOnly}>{srText}</legend>
				{options.map(
					({
						title,
						subtitle,
						description,
						id,
						expanded,
						icon,
						'data-testid': containerTestId,
						value,
					}) => {
						return (
							<div
								key={id}
								data-testid={containerTestId}
								className={cx(styles.container, expanded && styles.expanded)}
								data-expanded={expanded}
							>
								<input
									type='radio'
									id={id}
									name={name}
									value={value}
									className={fukkuStyles.srOnly}
									ref={ref}
									checked={expanded}
									onChange={handleChange}
								/>
								<label
									htmlFor={id}
									data-testid={`${id}.title`}
									className={cx(
										styles.title,
										!!icon && styles.row,
										expanded && styles.expanded,
										isFreeContent && styles.freeContent
									)}
								>
									<div className={styles.heading}>
										<div className={text.titleM}>{title}</div>
										{subtitle && <div>{subtitle}</div>}
									</div>
									{icon && <div className={styles.icon}>{icon}</div>}
								</label>
								{description && (
									<div
										inert={!expanded}
										className={cx(styles.content, expanded && styles.show)}
									>
										<div
											data-testid={`${id}.description`}
											className={styles.description}
										>
											{description}
										</div>
									</div>
								)}
							</div>
						)
					}
				)}
			</fieldset>
		)
	}
)
